import {
  Component,
  OnInit,
  ViewChild,
  Renderer2,
  Inject,
  HostListener,
  DOCUMENT
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { TranslateService } from '@ngx-translate/core';
import { Location } from '@angular/common';
import {
  LogonService,
  StorageService,
  Environment,
  EnterpriseBasketService,
  EnterpriseSearchService,
  WithSubscriptionComponent,
  UserService,
  HelperRoutines,
  ChatbotSearchDispatcherService
} from '@sabstravtech/obtservices/angular';
import { fromEvent } from 'rxjs';
import { UserData } from '@sabstravtech/obtservices/base';
import { NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';

import { VERSION } from '../../../../environments/version';
import { LanguageService } from '../../services/language.service';
import { Language } from '../../interfaces/language-types';
import { PageType } from '../../enum/page-type.enum';
import { ThemeService } from '../../services/theme.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent extends WithSubscriptionComponent implements OnInit {
  title = 'Lightning UK Login';
  modalSubscription: Promise<any>;
  private previousURL = '';
  isRadius = false;
  isRefreshed = false;
  displayScrollButton = false;
  displayItinerary = false;
  private ucRootObserver: MutationObserver | null = null;
  private ucShadowObserver: MutationObserver | null = null;

  constructor(
    private router: Router,
    public authService: LogonService,
    private storageService: StorageService,
    private cookieService: CookieService,
    private languageService: LanguageService,
    private translate: TranslateService,
    private environmentObt: Environment,
    private _location: Location,
    public basketService: EnterpriseBasketService,
    public searchService: EnterpriseSearchService,
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    private themeService: ThemeService,
    private userService: UserService,
    private helpers: HelperRoutines,
    private chatbotSearchDispatcher: ChatbotSearchDispatcherService,
    modalConfig: NgbModalConfig
  ) {
    super();
    modalConfig.ariaLabelledBy = 'modal-header';
    this.translate.addLangs(['en', 'fr', 'de']);
    this.translate.setDefaultLang('en');

    const lang: Language = this.languageService.getLanguageFromRoute();
    this.translate.use(lang.abre);
    this.themeService.setTheme();
    if (authService.isLightningEnv) {
      this.injectUserCentricsScript();
      this.subscribe(this.userService.fullUserDetails.userData, (userData) => {
        if (userData) {
          if ((userData as any).testing_account) {
            this.removeUserCentricsScript();
          } else {
            // this.userService.getCompanyById(userData.company_id.toString())
            //   .subscribe(companyData => {
            this.injectPendoAnalytics(environment.PendoApiKey, userData);
            // });
          }
        }
      });
    }
  }

  injectPendoAnalytics(apiKey: string, user: UserData): void {
    // Guard to ensure Pendo initializes only once
    if ((window as any).pendoInitialized) {
      console.log('PENDO: Already initialized, skipping re-initialization.');
      return;
    }

    (function (apiKey, agentUrl, agentSri) {
      (function (window, document, tagname, namespace, pendo) {
        let methods, index, length, script, calls;
        pendo = window[namespace] = window[namespace] || {};
        methods = ['initialize', 'identify', 'updateOptions', 'pageLoad'];

        // Create a queue for Pendo methods
        for (calls = pendo._q = [], index = 0, length = methods.length; index < length; ++index) {
          (function shim(method) {
            pendo[method] =
              pendo[method] ||
              function () {
                calls[method === methods[0] ? 'unshift' : 'push'](
                  [].concat.apply([method], arguments)
                );
              };
          })(methods[index]);
        }

        // Create and configure the script element
        script = document.createElement(tagname);
        script.async = true;
        script.crossOrigin = 'anonymous';
        if (agentSri) {
          script.integrity = agentSri;
        }
        script.src = agentUrl;
        document.head.appendChild(script);
      })(window, document, 'script', 'pendo');

      /**
       * Queue external config prior to handling `identify` call,
       * since `initialize` calls are prepended to the call queue (pendo._q)
       */
      (window as any).pendo.initialize('//cdn.pendo.io/agent/static/' + apiKey + '/config.json');

      /**
       * Call `pendo.identify` to supply visitor and account metadata
       */
      (window as any).pendo.identify({
        visitor: {
          id: user.username, // Required if user is logged in
          email: user.username, // Optional
          firstname: user.forename, // Optional
          lastname: user.surname, // Optional
          language: user.pref_lang, // Optional
          role: user.booker_type // Optional
        },
        account: {
          id: user.company_id, // Required if available
          name: user.company_name // Optional
        }
      });

      console.log('PENDO: Initialized and identified visitor and account.');

      // Set the guard to indicate initialization is complete
      (window as any).pendoInitialized = true;
    })(
      apiKey, // Used to fetch the `config.json` for the specified API key
      '//cdn.pendo.io/agent/releases/2.260.2/pendo.lightning.min.js', // Full URL to the agent
      `${environment.INTEGRITY_HASH}` // Calculated SRI hash
    );
  }

  injectUserCentricsScript = () => {
    // Check if the script is already injected
    if (!this.document.getElementById('usercentrics-cmp')) {
      const script = this.document.createElement('script');
      script.id = 'usercentrics-cmp';
      script.src = environment.UserCentricsUrl;
      script.setAttribute(
        'data-settings-id',
        environment.UserCentricsID // this is the ID????
      );
      script.async = true;
      this.document.body.appendChild(script);
      console.log(script);
      script.onload = () => {
        /* eslint-disable no-console */
        console.log('Usercentrics script loaded successfully');
      };
      script.onerror = () => {
        /* eslint-disable no-console */
        console.error('Failed to load the Usercentrics script');
      };
    }
    this.initUserCentricsA11yObserver();
  };

  removeUserCentricsScript = () => {
    const script = this.document.getElementById('usercentrics-cmp');
    if (script) {
      script.remove();
      console.log('UserCentrics script removed for testing account');
    }
    if (this.ucRootObserver) {
      this.ucRootObserver.disconnect();
      this.ucRootObserver = null;
    }
    if (this.ucShadowObserver) {
      this.ucShadowObserver.disconnect();
      this.ucShadowObserver = null;
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('UC_UI_INITIALIZED', this.applyUserCentricsAriaLabels);
      window.removeEventListener('UC_UI_VIEW_CHANGED', this.applyUserCentricsAriaLabels);
    }
  };

  @ViewChild('cookieLaw') public cookieLawEl;
  public dismiss(): void {
    if (this.cookieLawEl) {
      this.cookieLawEl.dismiss();
    }
  }

  async ngOnInit(): Promise<void> {
    this.chatbotSearchDispatcher.init();
    const clicks = fromEvent(document, 'click');
    const keyDown = fromEvent(document, 'keydown');

    this.authService.obtVersion = VERSION;
    const haveAgencyUserCookie = this.cookieService.check('agencyLoggedIn');
    console.log('have', haveAgencyUserCookie);
    // console.log('+++ Checking Agency Cookie: ', haveAgencyUserCookie, ' +++');

    // if we have cookie and not session - go to sso page

    if (haveAgencyUserCookie && !this.storageService.getSessionItem('Agency_User')) {
      // console.log('+++ We have the agency user cookie set and are missing the Agency_User session storage +++');
      this.router.navigate(['/sso']);
    }

    this.subscribe(keyDown, (event) => {
      const keyboardEvent = event as KeyboardEvent;
      if (
        !this.document.body.classList.contains('user-is-tabbing') &&
        keyboardEvent.key === 'Tab'
      ) {
        this.renderer.addClass(this.document.body, 'user-is-tabbing');
      }
    });

    this.subscribe(clicks, (event) => {
      const pointerEvent = event as PointerEvent;
      if (
        this.document.body.classList.contains('user-is-tabbing') &&
        pointerEvent.offsetX !== 0 &&
        pointerEvent.offsetY !== 0
      ) {
        // If user continues to click after tabbing we don't want the outline to appear anymore
        this.renderer.removeClass(this.document.body, 'user-is-tabbing');
      }

      /* THIS IS COVER FOR THIRD PARTY WCAG ISSUES, ngbTypeaheads automatically have the property
      "aria-multiline" but this isn't a property they should have and it causes a WCAG violation so this is
      just making sure the property is removed from them any time the user clicks onto a new page*/
      const multilines = document.querySelectorAll('[aria-multiline^="false"]');
      for (let x = 0; x < multilines.length; x++) {
        multilines[x].removeAttribute('aria-multiline');
      }
    });

    if (this.environmentObt.disable_console) {
      (<any>window).oldConsole = console.log;
      console.log = function () {};
      console.log('Test Unsuccessful!');
    } else {
      console.log('Console Enabled!');
    }

    this.subscribe(this.router.events, (event) => {
      if (event instanceof NavigationStart) {
        if (!this.previousURL || !this.previousURL.includes(event.url)) {
          this.previousURL = window.location.href;
          localStorage.setItem('prevURL', window.location.href);
        }
      }

      if (event instanceof NavigationEnd) {
        if (!this.isRefreshed) {
          this.checkResultRefresh(event.url);
          this.isRefreshed = true;
        }

        // WCAG ENT-18497: On SPA navigation, reset focus to top of page and announce page title
        setTimeout(() => {
          window.scrollTo(0, 0);
          this.document.documentElement.scrollTop = 0;
          this.document.body.scrollTop = 0;
          this.document.body.setAttribute('tabindex', '-1');
          this.document.body.focus({ preventScroll: true });
        }, 0);

        setTimeout(() => {
          this.announcePageTitle();
        }, 150);
      }
    });

    const titleElement = this.document.querySelector('title');
    if (titleElement) {
      const titleObserver = new MutationObserver(() => {
        this.announcePageTitle();
      });
      titleObserver.observe(titleElement, { childList: true, subtree: true, characterData: true });
    }

    this.isRadius = window.location.href.indexOf('radius') !== -1;

    this.subscribe(this.userService.fullUserDetails.allDetails, (all) => {
      this.displayItinerary = this.userService.isBasketReplacementUserFavouriteSet();
      if (all) {
        this.injectPostLoginScripts();
      }
    });
  }

  back() {
    this._location.back();
  }

  get displayBack(): boolean {
    return (
      window.location.href.includes('/results/') &&
      !window.location.href.includes('wellbeing') &&
      this.previousURL.includes('wellbeing')
    );
  }

  checkResultRefresh(url: string) {
    if (url.includes(PageType.Results) || url.includes(PageType.Itinerary)) {
      this.searchService.search_objects = this.storageService.getSessionItem('search_params');
    }
  }

  scrollToTop(): void {
    window.scrollTo(0, 0);
    const focusable: NodeListOf<HTMLElement> = this.document.body.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length) {
      focusable[0].focus();
    }
  }

  private announcePageTitle(): void {
    const readTitleEl = this.document.getElementById('readTitle');
    if (readTitleEl) {
      const currentTitle = this.document.title;
      if (currentTitle) {
        readTitleEl.textContent = '';
        setTimeout(() => {
          readTitleEl.textContent = currentTitle;
        }, 50);
      }
    }
  }

  @HostListener('window:scroll', ['$event']) onScrollEvent() {
    this.displayScrollButton = window.scrollY > window.innerHeight * 2;
  }

  getQueryParams(): { code: string; state: string } {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');

    return { code, state };
  }

  injectPostLoginScripts(): void {
    // Check if the script is already injected
    if (!this.document.getElementById('googlemaps-script')) {
      const script = this.document.createElement('script');
      script.id = 'googlemaps-script';
      script.src =
        'https://maps.googleapis.com/maps/api/js?key=AIzaSyC_I3019IO-Dzdx5Hn-7Hlp1socVWLYeno&libraries=places';
      this.document.body.appendChild(script);
      console.log(script);
      script.onload = () => {
        /* eslint-disable no-console */
        console.log('Googlemaps script loaded successfully');
      };
      script.onerror = () => {
        /* eslint-disable no-console */
        console.error('Failed to load the Googlemaps script');
      };
    }
    if (!this.document.getElementById('googleclusters-script')) {
      const script = this.document.createElement('script');
      script.id = 'googleclusters-script';
      script.src = 'https://unpkg.com/@googlemaps/markerclustererplus/dist/index.min.js';
      script.setAttribute(
        'integrity',
        'sha384-QhOaBRyAi2AaOmp+g91pOMssj8o/AqBZPDtRyeqokTzBc0E+gg0Lfs8tIq/Gc8Sz'
      );
      script.setAttribute('crossorigin', 'anonymous');
      this.document.body.appendChild(script);
      console.log(script);
      script.onload = () => {
        /* eslint-disable no-console */
        console.log('Google clusters script loaded successfully');
      };
      script.onerror = () => {
        /* eslint-disable no-console */
        console.error('Failed to load the Google clusters script');
      };
    }
  }

  private applyUserCentricsAriaLabels = (): void => {
    try {
      const ucRoot = this.document.getElementById('usercentrics-root');
      const root = ucRoot?.shadowRoot;
      if (!root) {
        return;
      }

      const buttons = root.querySelectorAll('button');
      buttons.forEach((btn) => {
        const testId = btn.getAttribute('data-testid');
        const text = btn.textContent?.trim().toLowerCase() || '';

        // If the button text already explicitly contains cookie/cookies, it has sufficient context
        if (text.includes('cookie')) {
          return;
        }

        if (testId === 'uc-save-button' || text === 'save settings') {
          if (btn.getAttribute('aria-label') !== 'Save Cookie Settings') {
            btn.setAttribute('aria-label', 'Save Cookie Settings');
          }
        } else if (testId === 'uc-deny-all-button' || text === 'deny' || text === 'deny all') {
          if (btn.getAttribute('aria-label') !== 'Deny Cookies') {
            btn.setAttribute('aria-label', 'Deny Cookies');
          }
        } else if (testId === 'uc-accept-all-button' || text === 'accept all') {
          if (btn.getAttribute('aria-label') !== 'Accept All Cookies') {
            btn.setAttribute('aria-label', 'Accept All Cookies');
          }
        } else if (testId === 'uc-more-button' || text === 'more information' || text === 'more') {
          if (btn.getAttribute('aria-label') !== 'More Cookie Settings') {
            btn.setAttribute('aria-label', 'More Cookie Settings');
          }
        } else if (testId === 'uc-customize-button' || text === 'customize') {
          if (btn.getAttribute('aria-label') !== 'Customize Cookie Settings') {
            btn.setAttribute('aria-label', 'Customize Cookie Settings');
          }
        }
      });
    } catch {
      // Ignore errors accessing shadow DOM
    }
  };

  private initUserCentricsA11yObserver = (): void => {
    if (typeof window === 'undefined' || !this.document) {
      return;
    }

    const attachToShadowRoot = (rootEl: HTMLElement): boolean => {
      const shadow = rootEl.shadowRoot;
      if (!shadow) {
        return false;
      }
      this.applyUserCentricsAriaLabels();
      if (this.ucShadowObserver) {
        this.ucShadowObserver.disconnect();
      }
      this.ucShadowObserver = new MutationObserver(() => {
        this.applyUserCentricsAriaLabels();
      });
      this.ucShadowObserver.observe(shadow, {
        childList: true,
        subtree: true
      });
      return true;
    };

    let observedRoot: HTMLElement | null = null;
    const checkAndAttach = (): void => {
      const rootEl = this.document.getElementById('usercentrics-root');
      if (rootEl && (rootEl !== observedRoot || !this.ucShadowObserver)) {
        if (attachToShadowRoot(rootEl)) {
          observedRoot = rootEl;
        }
      }
    };

    checkAndAttach();

    if (!this.ucRootObserver) {
      this.ucRootObserver = new MutationObserver(() => {
        checkAndAttach();
      });
      this.ucRootObserver.observe(this.document.body || this.document.documentElement, {
        childList: true,
        subtree: true
      });
    }

    window.addEventListener('UC_UI_INITIALIZED', this.applyUserCentricsAriaLabels);
    window.addEventListener('UC_UI_VIEW_CHANGED', this.applyUserCentricsAriaLabels);
  };
}
